import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography, SelectChangeEvent, Checkbox } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { AxiosError } from "axios";
import { Grid } from "@material-ui/core";
import submitTick from "../../assets/images/submitTick.svg";
import CustomModal from "../../components/common/customModal";
import CustomInput from "../../components/common/inputField";
import BikeDiscountSearchableSelect from "./bikeDiscountSearchableSelect";
import RELoader from "../../components/common/RELoader";
import bikeDiscountService from "../../services/bikeDiscountService";
import {
  BikeDiscountCityDealer,
  BikeDiscountEmployee,
  BikeDiscountModelCode,
} from "../../types/bikeDiscount";
import { useAppSelector } from "../../globalState/hooks";
import { LoginRootState } from "../../types/login";

const UNDERTAKING_PARAGRAPHS = [
  "This Confirmation and Undertaking is made by me (hereinafter ‘Employee’) resident of Address in favour of Royal Enfield, a unit of Eicher Motors Limited (hereinafter “Company”), a company with its corporate office at #96, Sector 32, Gurgaon, Haryana, 122001, and engaged in the business of manufacturing and selling motorbikes, with respect to the Royal Enfield Motorcycling Policy (hereinafter ‘Scheme’).",
  "The Employee is a permanent employee of the Company. The Employee has availed the Scheme offered by the Company to its employees, and pursuant to the aforementioned Scheme undertakes to purchase the motorcycle for his/her own personal use and enjoyment only. The Employee further undertakes not to sell the motorcycle purchased under the aforesaid Scheme to any third party for a period of two years.",
  "The employee has read and understood the motorcycle Policy’s rules and regulations. When completing the reimbursement Claim of RE Gear, during or after the purchase process of a motorcycle, the employee authorizes the company and/or its designee to contact the selling Company store to verify purchase information and from the vehicle registration authority to verify titling information. In case of any breach of, or dispute, arising out of this Confirmation and Undertaking, the Employee undertakes to be subject to the non-exclusive jurisdiction of the Courts of Delhi/Chennai, India.",
];

const BIKE_DISCOUNT_SUPPORT_EMAIL = "HRAppsSupport@royalenfield.com";
const API_FAILURE_USER_MESSAGE = `An issue has arisen affecting the data. Please contact support at ${BIKE_DISCOUNT_SUPPORT_EMAIL}.`;
const API_FAILURE_TYPE = 1;
const SKIP_TOAST = { skipErrorToast: true } as const;

const REONE_API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/?$/, "/");
const EMP_DISCOUNT_API_BASE = (
  process.env.REACT_APP_EMP_DISCOUNT_API_URL || ""
).replace(/\/?$/, "/");

const buildReoneApiPath = (path: string) =>
  `${REONE_API_BASE}${path.replace(/^\//, "")}`;

const buildEmpDiscountApiPath = (path: string) =>
  `${EMP_DISCOUNT_API_BASE}${path.replace(/^\//, "")}`;

interface BikeDiscountEnquiryModalProps {
  open: boolean;
  onClose: () => void;
}

interface BikeDiscountFormData {
  mobileNumber: string;
  city: string;
  dealerCode: string;
  dealerName: string;
  modelCode: string;
  modelName: string;
}

interface BikeDiscountFormErrors {
  mobileNumber?: string;
  city?: string;
  dealerCode?: string;
  modelCode?: string;
  agreement?: string;
}

interface SubmittedSummary {
  name: string;
  mobile: string;
  email: string;
  dealer: string;
  modelCode: string;
  category: string;
}

const BIKE_DISCOUNT_REMARKS = "Employee Bike Discount";
const BIKE_DISCOUNT_CATEGORY = "Employee Discount";

const initialFormData: BikeDiscountFormData = {
  mobileNumber: "",
  city: "",
  dealerCode: "",
  dealerName: "",
  modelCode: "",
  modelName: "",
};

const SubmittedInfoRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 1,
      py: 0.75,
    }}
  >
    <Typography sx={{ fontSize: "13px", color: "#DDDDDD", flexShrink: 0 }}>
      {label}:
    </Typography>
    <Typography
      sx={{
        fontSize: "14px",
        color: "#FFFFFF",
        fontWeight: 500,
        textAlign: "right",
        wordBreak: "break-word",
      }}
    >
      {value}
    </Typography>
  </Box>
);

const BikeDiscountEnquiryModal: React.FC<BikeDiscountEnquiryModalProps> = ({
  open,
  onClose,
}) => {
  const userDetails = useAppSelector(
    (state: LoginRootState) => state.authReducer.loginDetials
  );
  const [formData, setFormData] =
    useState<BikeDiscountFormData>(initialFormData);
  const [formErrors, setFormErrors] = useState<BikeDiscountFormErrors>({});
  const [btnLoading, setBtnLoading] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [isDealersLoading, setIsDealersLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(
    null
  );
  const [isSystemApiFailure, setIsSystemApiFailure] = useState(false);
  const [submittedSummary, setSubmittedSummary] =
    useState<SubmittedSummary | null>(null);
  const [employeeInfo, setEmployeeInfo] = useState<BikeDiscountEmployee | null>(
    null
  );
  const employeeInfoRef = useRef<BikeDiscountEmployee | null>(null);
  const [dealers, setDealers] = useState<BikeDiscountCityDealer[]>([]);
  const [models, setModels] = useState<BikeDiscountModelCode[]>([]);
  const [cityOptions, setCityOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [agreedToUndertaking, setAgreedToUndertaking] = useState(false);
  const [showUndertaking, setShowUndertaking] = useState(false);

  const dealerOptions = dealers.map((dealer) => ({
    label: dealer.dealerName,
    value: dealer.dealerCode,
  }));

  const modelOptions = models.map((model) => ({
    label: `${model.modelName} (${model.modelCode})`,
    value: model.modelCode,
  }));

  const resetFormState = useCallback(() => {
    setFormData(initialFormData);
    setFormErrors({});
    setEmployeeInfo(null);
    employeeInfoRef.current = null;
    setDealers([]);
    setModels([]);
    setCityOptions([]);
    setAgreedToUndertaking(false);
    setShowUndertaking(false);
  }, []);

  useEffect(() => {
    if (!open) {
      setShowSuccess(false);
      setSubmitErrorMessage(null);
      setIsSystemApiFailure(false);
      setSubmittedSummary(null);
      resetFormState();
    }
  }, [open, resetFormState]);

  const getErrorMessage = (error: unknown, fallback: string) => {
    const axiosError = error as AxiosError<{ message?: string; error?: string }>;
    return (
      axiosError?.response?.data?.message ||
      axiosError?.response?.data?.error ||
      axiosError?.message ||
      fallback
    );
  };

  const reportApiFailure = useCallback(
    async (
      apiName: string,
      errorMessage: string,
      employeeOverride?: BikeDiscountEmployee | null
    ) => {
      setIsSystemApiFailure(true);
      setSubmitErrorMessage(API_FAILURE_USER_MESSAGE);

      const employee = employeeOverride ?? employeeInfoRef.current;
      try {
        await bikeDiscountService.sendApiFailureEmail({
          applicationId: 0,
          employeeId: employee?.employeeId || userDetails?.userId || "",
          employeeCode: employee?.employeeCode || userDetails?.userId || "",
          employeeName: employee?.employeeName || userDetails?.fullName || "",
          apiName,
          failureType: API_FAILURE_TYPE,
          errorMessage: errorMessage || "Unknown API failure",
        });
      } catch {
        // Do not block the user if failure-email API itself fails.
      }
    },
    [userDetails?.fullName, userDetails?.userId]
  );

  const fetchDealersByCity = useCallback(
    async (city: string) => {
      if (!city) {
        setDealers([]);
        return;
      }

      const apiName = buildEmpDiscountApiPath(
        `dealers/${encodeURIComponent(city)}`
      );
      setIsDealersLoading(true);
      try {
        const response = await bikeDiscountService.getDealersByCity(
          city,
          SKIP_TOAST
        );
        if (response?.data?.status === "Success" && response?.data?.dealers) {
          setDealers(response.data.dealers);
        } else {
          setDealers([]);
          await reportApiFailure(
            apiName,
            response?.data?.message || "Failed to load dealers."
          );
        }
      } catch (error) {
        setDealers([]);
        await reportApiFailure(
          apiName,
          getErrorMessage(error, "Failed to load dealers.")
        );
      } finally {
        setIsDealersLoading(false);
      }
    },
    [reportApiFailure]
  );

  useEffect(() => {
    if (!open || showSuccess) return;

    const fetchInitialData = async () => {
      setIsFormLoading(true);
      let loadedEmployee: BikeDiscountEmployee | null = null;

      try {
        const employeeRes = await bikeDiscountService.getEmployeeById(
          SKIP_TOAST
        );
        if (employeeRes?.data?.success && employeeRes?.data?.data) {
          loadedEmployee = employeeRes.data.data;
          employeeInfoRef.current = loadedEmployee;
          setEmployeeInfo(loadedEmployee);
          setFormData((prev) => ({
            ...prev,
            mobileNumber:
              loadedEmployee?.phoneNumber?.trim() || prev.mobileNumber,
          }));
        } else {
          await reportApiFailure(
            buildReoneApiPath("BikeDiscount/getEmployeeById"),
            employeeRes?.data?.message || "Failed to load employee information."
          );
        }
      } catch (error) {
        await reportApiFailure(
          buildReoneApiPath("BikeDiscount/getEmployeeById"),
          getErrorMessage(error, "Failed to load employee information.")
        );
      }

      try {
        const citiesRes = await bikeDiscountService.getCities(SKIP_TOAST);
        if (citiesRes?.data?.status === "Success" && citiesRes?.data?.cities) {
          setCityOptions(
            citiesRes.data.cities.map((city) => ({
              label: city,
              value: city,
            }))
          );
        } else {
          await reportApiFailure(
            buildEmpDiscountApiPath("cities"),
            "Failed to load cities.",
            loadedEmployee
          );
        }
      } catch (error) {
        await reportApiFailure(
          buildEmpDiscountApiPath("cities"),
          getErrorMessage(error, "Failed to load cities."),
          loadedEmployee
        );
      }

      try {
        const modelsRes = await bikeDiscountService.getModelCodes(SKIP_TOAST);
        if (modelsRes?.data?.models) {
          setModels(modelsRes.data.models);
        } else {
          await reportApiFailure(
            buildEmpDiscountApiPath("models"),
            "Failed to load models.",
            loadedEmployee
          );
        }
      } catch (error) {
        await reportApiFailure(
          buildEmpDiscountApiPath("models"),
          getErrorMessage(error, "Failed to load models."),
          loadedEmployee
        );
      } finally {
        setIsFormLoading(false);
      }
    };

    fetchInitialData();
  }, [open, showSuccess, reportApiFailure]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const sanitized =
      name === "mobileNumber" ? value.replace(/\D/g, "").slice(0, 10) : value;
    setFormData((prev) => ({ ...prev, [name]: sanitized }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleCityChange = (event: SelectChangeEvent<string | number>) => {
    const city = String(event.target.value);

    setFormData((prev) => ({
      ...prev,
      city,
      dealerCode: "",
      dealerName: "",
    }));
    setFormErrors((prev) => ({ ...prev, city: "", dealerCode: "" }));
    setDealers([]);

    if (city) {
      fetchDealersByCity(city);
    }
  };

  const handleDealerChange = (event: SelectChangeEvent<string | number>) => {
    const dealerCode = String(event.target.value);
    const selectedDealer = dealers.find((d) => d.dealerCode === dealerCode);

    setFormData((prev) => ({
      ...prev,
      dealerCode,
      dealerName: selectedDealer?.dealerName ?? "",
    }));
    setFormErrors((prev) => ({ ...prev, dealerCode: "" }));
  };

  const handleModelChange = (event: SelectChangeEvent<string | number>) => {
    const modelCode = String(event.target.value);
    const selectedModel = models.find((m) => m.modelCode === modelCode);

    setFormData((prev) => ({
      ...prev,
      modelCode,
      modelName: selectedModel?.modelName ?? "",
    }));
    setFormErrors((prev) => ({ ...prev, modelCode: "" }));
  };

  const validateForm = () => {
    const errors: BikeDiscountFormErrors = {};
    const mobileRegex = /^[6-9]\d{9}$/;

    if (!formData.mobileNumber.trim()) {
      errors.mobileNumber = "Mobile number is required.";
    } else if (!mobileRegex.test(formData.mobileNumber.trim())) {
      errors.mobileNumber = "Enter a valid 10-digit mobile number.";
    }

    if (!formData.city) errors.city = "Please select a city.";
    if (!formData.dealerCode) {
      errors.dealerCode = "Please select a dealer.";
    }
    if (!formData.modelCode) {
      errors.modelCode = "Please select a model code.";
    }
    if (!agreedToUndertaking) {
      errors.agreement =
        "Please agree to the Confirmation and Undertaking.";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleClose = () => {
    setShowSuccess(false);
    setSubmitErrorMessage(null);
    setIsSystemApiFailure(false);
    setSubmittedSummary(null);
    resetFormState();
    onClose();
  };

  const handleModalClose = () => {
    handleClose();
  };

  const handleErrorDismiss = () => {
    handleClose();
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setBtnLoading(true);
    setSubmitErrorMessage(null);
    setIsSystemApiFailure(false);
    try {
      const response = await bikeDiscountService.submitBikeDiscount({
        city: formData.city,
        dealerCode: formData.dealerCode,
        dealerName: formData.dealerName,
        modelCode: formData.modelCode,
        modelName: formData.modelName,
        phoneNumber: formData.mobileNumber.trim(),
        remarks: BIKE_DISCOUNT_REMARKS,
      });

      if (response?.data?.success) {
        setSubmittedSummary({
          name: employeeInfo?.employeeName ?? "",
          mobile: formData.mobileNumber.trim(),
          email: employeeInfo?.email ?? "",
          dealer: formData.dealerName,
          modelCode: formData.modelName
            ? `${formData.modelName} (${formData.modelCode})`
            : formData.modelCode,
          category: BIKE_DISCOUNT_CATEGORY,
        });
        setShowSuccess(true);
      } else {
        await reportApiFailure(
          buildReoneApiPath("BikeDiscount/submit"),
          response?.data?.message || "Unable to submit bike discount enquiry."
        );
      }
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string; error?: string }>;
      if (axiosError.response?.status === 400) {
        setIsSystemApiFailure(false);
        setSubmitErrorMessage(
          getErrorMessage(error, "Unable to submit bike discount enquiry.")
        );
        return;
      }

      await reportApiFailure(
        buildReoneApiPath("BikeDiscount/submit"),
        getErrorMessage(error, "Unable to submit bike discount enquiry.")
      );
    } finally {
      setBtnLoading(false);
    }
  };

  const renderErrorView = () => {
    if (!submitErrorMessage) return null;

    return (
      <Box sx={{ textAlign: "center", px: 1, pb: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 2.5,
            mt: 1,
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              bgcolor: "#E12013",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 44, color: "#FFFFFF" }} />
          </Box>
        </Box>

        <Typography
          sx={{ fontSize: "18px", fontWeight: 700, color: "#FFFFFF", mb: 1 }}
        >
          {isSystemApiFailure ? "Something Went Wrong" : "Submission Failed"}
        </Typography>
        <Typography
          sx={{
            fontSize: "13px",
            color: "#CCCCCC",
            mb: 3,
            px: 2,
            lineHeight: 1.5,
          }}
        >
          {isSystemApiFailure
            ? "We couldn\u2019t complete your request."
            : "We couldn\u2019t process your bike purchase request."}
        </Typography>

        <Box
          sx={{
            bgcolor: "#4A4A4A",
            borderRadius: "4px",
            p: 2.5,
            textAlign: "left",
            mx: { xs: 0, sm: 2 },
          }}
        >
          {isSystemApiFailure ? (
            <Typography
              sx={{
                fontSize: "13px",
                color: "#CCCCCC",
                lineHeight: 1.6,
                wordBreak: "break-word",
              }}
            >
              An issue has arisen affecting the data. Please contact support at{" "}
              <Box
                component="a"
                href={`mailto:${BIKE_DISCOUNT_SUPPORT_EMAIL}`}
                sx={{
                  color: "#f1da47",
                  textDecoration: "underline",
                  wordBreak: "break-all",
                  fontWeight: 500,
                  "&:hover": {
                    color: "#ffe566",
                  },
                }}
              >
                {BIKE_DISCOUNT_SUPPORT_EMAIL}
              </Box>
              .
            </Typography>
          ) : (
            <Typography
              sx={{
                fontSize: "13px",
                color: "#CCCCCC",
                lineHeight: 1.6,
                wordBreak: "break-word",
              }}
            >
              {submitErrorMessage}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  const renderSuccessView = () => {
    if (!submittedSummary) return null;

    const summaryRows = [
      { label: "Name", value: submittedSummary.name },
      { label: "Mobile", value: submittedSummary.mobile },
      { label: "Email", value: submittedSummary.email },
      { label: "Dealer", value: submittedSummary.dealer },
      { label: "Model", value: submittedSummary.modelCode },
      { label: "Category", value: submittedSummary.category },
    ];

    return (
      <Box sx={{ textAlign: "center", px: 1, pb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 2.5,
            mt: 1,
          }}
        >
          <img
            src={submitTick}
            alt="Submission successful"
            style={{ width: 80, height: 80 }}
          />
        </Box>

        <Typography
          sx={{ fontSize: "18px", fontWeight: 700, color: "#FFFFFF", mb: 1 }}
        >
          Submission Successful!
        </Typography>
        <Typography
          sx={{
            fontSize: "13px",
            color: "#CCCCCC",
            mb: 3,
            px: 2,
            lineHeight: 1.5,
          }}
        >
          Your bike discount enquiry has been successfully submitted.
        </Typography>

        <Box
          sx={{
            bgcolor: "#4A4A4A",
            borderRadius: "4px",
            p: 2.5,
            textAlign: "left",
            mx: { xs: 0, sm: 2 },
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              color: "#FFFFFF",
              mb: 1.5,
            }}
          >
            Submitted Information
          </Typography>
          {summaryRows.map((row) => (
            <SubmittedInfoRow
              key={row.label}
              label={row.label}
              value={row.value}
            />
          ))}
        </Box>
      </Box>
    );
  };

  const renderFormView = () => {
    if (isFormLoading) {
      return <RELoader minHeight={280} />;
    }

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <CustomInput
            label="Mobile Number"
            type="text"
            name="mobileNumber"
            placeholder="Enter mobile number"
            value={formData.mobileNumber}
            onChange={handleInputChange}
            error={formErrors.mobileNumber}
            required
            maxLength={10}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <BikeDiscountSearchableSelect
            label="City"
            name="city"
            value={formData.city}
            onChange={handleCityChange}
            options={cityOptions}
            placeHolder="Select City"
            searchPlaceholder="Search city"
            error={formErrors.city}
            required
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <BikeDiscountSearchableSelect
            label="Dealer Name"
            value={formData.dealerCode || ""}
            onChange={handleDealerChange}
            options={dealerOptions}
            placeHolder={
              isDealersLoading
                ? "Loading dealers..."
                : formData.city
                  ? "Select Dealer"
                  : "Select city first"
            }
            searchPlaceholder="Search dealer"
            error={formErrors.dealerCode}
            required
            disabled={!formData.city}
            loading={isDealersLoading}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <BikeDiscountSearchableSelect
            label="Model Code"
            value={formData.modelCode || ""}
            onChange={handleModelChange}
            options={modelOptions}
            placeHolder="Select Model Code"
            searchPlaceholder="Search model"
            error={formErrors.modelCode}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Checkbox
              checked={agreedToUndertaking}
              onChange={(event) => {
                setAgreedToUndertaking(event.target.checked);
                setFormErrors((prev) => ({ ...prev, agreement: "" }));
              }}
              sx={{
                p: 0,
                mr: 1,
                color: "#CCCCCC",
                "&.Mui-checked": { color: "#E12013" },
              }}
            />
            <Typography sx={{ fontSize: "13px", color: "#CCCCCC" }}>
              I agree to the{" "}
              <Box
                component="span"
                onClick={() => setShowUndertaking(true)}
                sx={{
                  color: "#f1da47",
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                Confirmation and Undertaking
              </Box>
            </Typography>
          </Box>
          {formErrors.agreement && (
            <Typography
              className="f-12"
              sx={{ color: "#d32f2f", mt: 0.5 }}
            >
              {formErrors.agreement}
            </Typography>
          )}
        </Grid>
      </Grid>
    );
  };

  const renderUndertakingView = () => (
    <Box>
      {UNDERTAKING_PARAGRAPHS.map((paragraph, index) => (
        <Typography
          key={index}
          sx={{
            fontSize: "13px",
            color: "#CCCCCC",
            lineHeight: 1.7,
            mb: index === UNDERTAKING_PARAGRAPHS.length - 1 ? 0 : 2,
          }}
        >
          {paragraph}
        </Typography>
      ))}
    </Box>
  );

  const showResultView = showSuccess || !!submitErrorMessage;

  return (
    <>
      <CustomModal
        open={open}
        handleClose={handleModalClose}
        title={showResultView ? "" : "Employee Bike Purchase Request"}
        subtitle={
          showResultView
            ? undefined
            : "Fill in your details to apply"
        }
        onSave={submitErrorMessage ? handleErrorDismiss : handleSubmit}
        saveButtonName={submitErrorMessage ? "OK" : "Submit"}
        width={
          submitErrorMessage ? 520 : showSuccess ? 460 : 840
        }
        btnLoading={btnLoading}
        isSaveButtonShow={!showSuccess && !isFormLoading}
        borderBottom={showResultView ? "0px" : undefined}
      >

        {showSuccess
          ? renderSuccessView()
          : submitErrorMessage
            ? renderErrorView()
            : renderFormView()}
      </CustomModal>
      <CustomModal
        open={showUndertaking}
        handleClose={() => setShowUndertaking(false)}
        title="Confirmation and Undertaking"
        onSave={() => setShowUndertaking(false)}
        width={840}
        isSaveButtonShow={false}
      >
        {renderUndertakingView()}
      </CustomModal>
    </>
  );
};

export default BikeDiscountEnquiryModal;
